<template>
  <div class="tech-path-detail">
    <!-- 加载状态 -->
    <div v-if="isLoading" class="detail-loading">
      <el-icon class="loading-icon"><Loading /></el-icon>
      <span>正在加载技术路径详情...</span>
    </div>
    <!-- 错误状态 -->
    <div v-else-if="loadError" class="detail-error">
      <el-alert :title="loadError" type="error" show-icon :closable="false" />
      <div class="detail-error__actions">
        <el-button type="primary" @click="back">返回分析结果</el-button>
      </div>
    </div>
    <template v-else>
    <!-- 头部区域 -->
    <div class="detail-header">
      <div class="header-content">
        <h2 class="path-title">{{ techPath?.title || '技术路径详情' }}</h2>
        <p class="path-description">{{ techPath?.description || '暂无描述' }}</p>
        <div class="path-tag" v-if="techPath?.solutionDescription">
          <el-tag type="success">{{ techPath.solutionDescription }}</el-tag>
        </div>
        <div class="path-tags" v-if="techPath?.tags && techPath.tags.length > 0">
          <el-tag 
            v-for="(tag, index) in techPath.tags" 
            :key="index" 
            size="small" 
            class="tag-item"
          >
            {{ tag }}
          </el-tag>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="detail-content">
      <!-- 优缺点分析 -->
      <div class="section" v-if="(techPath?.pros && techPath.pros.length > 0) || (techPath?.cons && techPath.cons.length > 0)">
        <div class="pros-cons-grid">
          <div class="pros-card" v-if="techPath?.pros && techPath.pros.length > 0">
            <h4 class="pros-title">优点</h4>
            <ul class="pros-list">
              <li v-for="(pro, index) in techPath.pros" :key="index" class="pros-item">{{ pro }}</li>
            </ul>
          </div>
          <div class="cons-card" v-if="techPath?.cons && techPath.cons.length > 0">
            <h4 class="cons-title">缺点/风险</h4>
            <ul class="cons-list">
              <li v-for="(con, index) in techPath.cons" :key="index" class="cons-item">{{ con }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 未生成完整详情：提示 + 手动生成按钮（位于优缺点与方案详情之间） -->
      <div v-if="!isLoading && !loadError && !detailLoaded && !detailLoading" class="detail-missing">
        <div class="detail-missing__text">
          <h3>该技术路径尚未生成完整详情</h3>
          <p>当前仅展示基础信息，生成后将包含技术逻辑、结构方案、适用场景、工程落地要点、风险与TRIZ原理等内容</p>
        </div>
        <el-button type="primary" size="large" class="detail-missing__btn" @click="generateDetail">
          生成完整详情
        </el-button>
      </div>

      <!-- 正在生成完整详情（位于优缺点与方案详情之间） -->
      <div v-if="!isLoading && !loadError && detailLoading" class="detail-generating">
        <div class="detail-generating__bar" aria-hidden="true"><span></span></div>
        <h3>正在生成技术方案详情</h3>
        <p>模型正在梳理技术逻辑、结构方案和工程落地要点，请稍候…</p>
      </div>

      <!-- 生成失败提示 -->
      <el-alert v-if="detailError" :title="detailError" type="error" show-icon :closable="false" class="detail-error-banner" />

      <!-- 方案详情图片 -->
      <div class="section">
        <h3 class="section-title">方案详情</h3>
        <div v-if="imageGenerating" class="image-loading-text">正在使用视觉模型生成方案示意图，约需 1-3 分钟…</div>
        <div class="image-grid">
          <div 
            v-for="(image, index) in displayImages" 
            :key="index" 
            class="detail-image"
          >
            <img
              :src="image"
              :alt="`方案示意图${index + 1}`"
              class="image-item"
              @error="handleImageError($event, image)"
            >
          </div>
        </div>
        <el-empty
          v-if="!imageGenerating && displayImages.length === 0"
          description="暂无方案示意图"
          :image-size="72"
        />
      </div>

      <!-- 技术逻辑 -->
      <div class="section">
        <h3 class="section-title">技术逻辑</h3>
        <div class="logic-card">
          <p v-for="(para, index) in splitParagraphs(techPath?.technicalLogic || '暂无技术逻辑描述')" :key="index" class="logic-text">{{ para }}</p>
        </div>
      </div>

      <!-- 结构方案 -->
      <div class="section">
        <h3 class="section-title">结构方案</h3>
        <div class="structure-card">
          <p v-for="(para, index) in splitStructurePlan(techPath?.structurePlan || '暂无结构方案描述')" :key="index" class="structure-text">{{ para }}</p>
        </div>
      </div>

      <!-- 适用场景 -->
      <div class="section">
        <h3 class="section-title">适用场景</h3>
        <div class="scenario-card">
          <ul class="scenario-list">
            <li 
              v-for="(scenario, index) in techPath?.applicationScenarios || []" 
              :key="index" 
              class="scenario-item"
            >
              {{ scenario }}
            </li>
          </ul>
        </div>
      </div>

      <!-- 工程落地要点 -->
      <div class="section">
        <h3 class="section-title">工程落地要点</h3>
        <div class="engineering-card">
          <ol class="engineering-list">
            <li 
              v-for="(point, index) in techPath?.engineeringPoints || []" 
              :key="index" 
              class="engineering-item"
            >
              {{ point }}
            </li>
          </ol>
        </div>
      </div>

      <!-- 风险点与缓解措施 -->
      <div class="section">
        <h3 class="section-title">风险点与缓解措施</h3>
        <div class="risk-card">
          <ul class="risk-list">
            <li 
              v-for="(item, index) in techPath?.riskMitigations || []" 
              :key="index" 
              class="risk-item"
            >
              <strong>风险点：</strong>{{ item.risk }}<br>
              <strong>缓解措施：</strong>{{ item.mitigation }}
            </li>
          </ul>
        </div>
      </div>

      <!-- 应用的TRIZ原理 -->
      <div class="section">
        <h3 class="section-title">应用的TRIZ原理</h3>
        <div class="triz-grid">
          <el-collapse v-model="activeNames" accordion>
            <el-collapse-item 
              v-for="(principle, index) in techPath?.trizPrinciples || []" 
              :key="index" 
              :title="`${code+principle.number}`" 
              :name="(index + 1).toString()"
            >
              <div class="triz-content">
                <p v-for="(para, pi) in splitByNumber(principle.description)" :key="pi" class="triz-para">{{ para }}</p>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>

      <!-- 原理总结 -->
      <div class="section">
        <h3 class="section-title">原理总结</h3>
        <div class="summary-card">
          <p class="summary-text">{{ techPath?.principleSummary || '暂无原理总结描述' }}</p>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ArrowLeft, Loading } from '@element-plus/icons-vue'
import { useRoute } from 'vue-router' 
import { ElMessage } from 'element-plus'
import { updateTechnicalPath } from '@/api/persistence'
// 定义组件接收的props
const props = defineProps({
  techPath: {
    type: Object,
    default: () => ({})
  }
})
const code = '#'

// 定义组件发射的事件
const emit = defineEmits(['back'])
const route = useRoute()
// 技术路径数据：从后端按ID查询
const techPath = ref({})
const isLoading = ref(true)
const loadError = ref('')
// 配图生成中（原来这个页面没有任何“生成中”提示，生成期间就是一个空白框）
const imageGenerating = ref(false)
// 只渲染有效地址：库里的 images 可能混进空串/null（DB 读取路径不过滤），会导致 <img src=""> 白框
const displayImages = computed(() =>
  Array.isArray(techPath.value?.images) ? techPath.value.images.filter(Boolean) : []
)

// 修复模型输出的"伪JSON"（单引号/无引号键），转成标准JSON数组
const parseTrizPrinciples = (raw) => {
  if (Array.isArray(raw)) return raw
  if (!raw) return []
  let str = typeof raw === 'string' ? raw : JSON.stringify(raw)
  try {
    const parsed = JSON.parse(str)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    // 伪JSON修复：单引号->双引号、无引号键->加引号
    try {
      str = str
        .replace(/,\s*}/g, '}')  // 去掉尾部逗号
        .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')  // 键加引号
        .replace(/'/g, '"')  // 单引号->双引号
      const parsed = JSON.parse(str)
      return Array.isArray(parsed) ? parsed : []
    } catch (e2) {
      console.error('TRIZ原理解析失败:', str.slice(0, 100))
      return []
    }
  }
}

// 长文本按序号切分成段落数组（兼容 \n、以及 "1." "2." "（1）" 等序号），每个序号另起一段
const splitByNumber = (str) => {
  if (!str) return []
  let s = String(str)
  // 先按换行拆（如果有）
  const lines = s.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  if (lines.length > 1) return lines
  // 无换行：按序号拆（1. 2. 3. / 1、 2、 / （1）（2））
  const parts = s.split(/(?=\d{1,2}[\.、．]\s*)/).map(p => p.trim()).filter(Boolean)
  return parts.length > 1 ? parts : (s ? [s] : [])
}

// 详情长文本按换行拆成段落数组（模型按要求输出至少4段，\n 由多个 <p> 渲染为独立段落）
const splitParagraphs = (str) => {
  if (!str) return []
  return String(str).split(/\r?\n+/).map(l => l.trim()).filter(Boolean)
}

// 结构方案：按换行分段后，再把段内"1.xxx；2.xxx"形式的编号步骤拆成多行（避免误拆 0.3mm 等数值）
const splitStructurePlan = (text) => {
  if (!text) return []
  return splitParagraphs(text).flatMap((para) => {
    // 仅当出现"空格/分号/冒号 + 1-99 + 点号"的编号序列时才拆行
    if (!/[\s；：][1-9]\d?[\.、．]/.test(para)) return [para]
    const parts = para
      .split(/(?<=[\s；：])(?=[1-9]\d?[\.、．])/)
      .map(s => s.replace(/^[\s；：]+/, '').trim())
      .filter(Boolean)
    return parts.length > 1 ? parts : [para]
  })
}

// 前端 techPath -> 后端落库字段（与 Process 页一致）
const mapTechPathToBackend = (path = {}, projectId) => ({
  projectId,
  title: path.title || '',
  description: path.description || '',
  tags: Array.isArray(path.tags) ? path.tags : [],
  technicalLogic: path.technicalLogic || '',
  structuralScheme: path.structurePlan || '',
  usageScenarios: Array.isArray(path.applicationScenarios) ? path.applicationScenarios.join('\n') : '',
  risksMitigation: Array.isArray(path.riskMitigations) ? path.riskMitigations : [],
  prosCons: { pros: Array.isArray(path.pros) ? path.pros : [], cons: Array.isArray(path.cons) ? path.cons : [] },
  trizPrinciplesUsed: Array.isArray(path.trizPrinciples) ? path.trizPrinciples : [],
  principleSummary: path.principleSummary || '',
  isRecommended: path.isRecommended ? 1 : 0,
  progress: path.progress ?? 50,
  solutionDescription: path.solutionDescription || '',
  patentSearchParams: path.patentSearchParams || {},
  engineeringPoints: Array.isArray(path.engineeringPoints) ? path.engineeringPoints.join('\n') : (path.engineeringPoints || ''),
  images: Array.isArray(path.images) ? path.images : []
})

// 是否已生成完整详情（核心字段齐全才算）
const detailLoaded = computed(() => {
  const t = techPath.value
  return !!(t && t.technicalLogic && t.structurePlan)
})
const detailLoading = ref(false)
const detailError = ref('')

// 手动生成完整详情：成功后合并到当前路径并静默落库，下次打开直接展示
const generateDetail = async () => {
  if (!techPath.value?.id) {
    detailError.value = '缺少路径ID，无法生成'
    return
  }
  detailLoading.value = true
  detailError.value = ''
  const controller = new AbortController()
  const timeoutTimer = setTimeout(() => controller.abort(), 200 * 1000)
  try {
    const response = await fetch('/api/result/generate-tech-path-detail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: '', techPath: techPath.value }),
      signal: controller.signal
    })
    if (!response.ok) throw new Error(`详情接口请求失败，状态码: ${response.status}`)
    const data = await response.json()
    if (data.techPath) {
      techPath.value = { ...techPath.value, ...data.techPath }
      // 静默落库（不阻塞 UI）：按正式ID更新该路径记录，projectId 用真实项目ID（后端 @NotNull 校验）
      updateTechnicalPath(techPath.value.id, mapTechPathToBackend(techPath.value, techPath.value.projectId))
        .then(() => console.log('技术路径详情已落库'))
        .catch((e) => console.error('详情落库失败（静默）:', e))
    } else {
      throw new Error('生成结果为空，请重试')
    }
  } catch (error) {
    console.error('生成技术路径详情失败:', error)
    detailError.value = error.name === 'AbortError'
      ? '详情生成超时（200秒），请重试'
      : ('详情生成失败：' + (error.message || '请重试'))
  } finally {
    clearTimeout(timeoutTimer)
    detailLoading.value = false
  }
}

// 后端字段 -> 前端 techPath 对象（与 SimilarPatents/TechPathDetail 对齐）
const mapBackendToFront = (p) => ({
  id: p.id,
  projectId: p.projectId,
  title: p.title || '',
  description: p.description || '',
  tags: Array.isArray(p.tags) ? p.tags : [],
  pros: Array.isArray(p.prosCons?.pros) ? p.prosCons.pros : [],
  cons: Array.isArray(p.prosCons?.cons) ? p.prosCons.cons : [],
  isRecommended: p.isRecommended === 1,
  solutionDescription: p.solutionDescription || '',
  patentSearchParams: p.patentSearchParams || {},
  technicalLogic: p.technicalLogic || '',
  structurePlan: p.structuralScheme || '',
  applicationScenarios: splitByNumber(p.usageScenarios),
  engineeringPoints: splitByNumber(p.engineeringPoints),
  riskMitigations: Array.isArray(p.risksMitigation) ? p.risksMitigation : [],
  trizPrinciples: parseTrizPrinciples(p.trizPrinciplesUsed),
  principleSummary: p.principleSummary || '',
  images: Array.isArray(p.images) ? p.images : []
})

onMounted(async () => {
  const pathId = route.query.pathId || ''
  if (!pathId) {
    loadError.value = '缺少路径ID'
    isLoading.value = false
    return
  }
  try {
    // 调后端按ID查询技术路径详情（GET /technical-path/{id}）
    const resp = await fetch(`/innovation/innovation/technical-path/${pathId}`, {
      headers: { 'Content-Type': 'application/json' }
    })
    if (!resp.ok) throw new Error(`查询失败: ${resp.status}`)
    const data = await resp.json()
    const tp = data.result || data
    if (tp && tp.id) {
      techPath.value = mapBackendToFront(tp)
    } else {
      loadError.value = '路径不存在'
      return
    }
  } catch (e) {
    console.error('加载技术路径详情失败:', e)
    loadError.value = '加载失败：' + (e.message || '请重试')
    return
  } finally {
    isLoading.value = false
  }

  // 无配图时按需生成（与 Process 页"查看详情"行为一致），成功后写库
  if (!Array.isArray(techPath.value.images) || techPath.value.images.length === 0) {
    void generateImages(techPath.value)
  }
})

// 构造配图提示词（黑白专利附图风格，与 Process 页一致）
const buildImagePrompt = (path) => {
  const technicalLogic = String(path?.technicalLogic || '').slice(0, 1200)
  const structurePlan = String(path?.structurePlan || '').slice(0, 1600)
  return `请根据以下技术方案生成一张黑白专利附图风格的技术结构示意图，只生成一张图。要求：白色背景、黑色线稿、清晰的剖视图或结构示意图、部件用数字编号标注、突出部件连接关系和工作路径，不要艺术化渲染，不要照片质感，不要生成任何可读文字、标题、图例或水印，避免乱码。

技术路径：${path?.title || ''}
方案摘要：${path?.description || ''}
技术逻辑：${technicalLogic}
结构方案：${structurePlan}`
}

// 图片加载失败：留痕（地址失效/跨域/403 都会走到这里），并给出可见的破图兜底
const handleImageError = (event, image) => {
  console.warn('[方案详情] 配图加载失败（地址不可访问或已失效）:', { image })
  if (event?.target) {
    event.target.classList.add('image-item--broken')
  }
}

// 按需生成配图（轮询任务结果，成功后更新并写库）
const generateImages = async (path) => {
  if (!path?.id) return
  imageGenerating.value = true
  try {
    const startResponse = await fetch('/api/result/generate-tech-path-images', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt: buildImagePrompt(path) })
    })
    if (!startResponse.ok) throw new Error(`图片任务提交失败，状态码: ${startResponse.status}`)
    const startData = await startResponse.json()
    if (!startData.taskId) throw new Error('图片任务未返回任务ID')
    console.log('[方案详情] 配图任务已提交:', { pathId: path.id, taskId: startData.taskId })

    const maxPolls = 90
    for (let attempt = 0; attempt < maxPolls; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000))
      const pollResponse = await fetch(`/api/result/tech-path-images/${encodeURIComponent(startData.taskId)}`)
      if (!pollResponse.ok) continue
      const pollData = await pollResponse.json()
      if (pollData.status === 'SUCCEEDED') {
        const images = Array.isArray(pollData.images) ? pollData.images.filter(Boolean).slice(0, 1) : []
        if (images.length > 0) {
          console.log('[方案详情] 配图已生成:', images)
          techPath.value = { ...techPath.value, images }
          // 图片写库（静默）：下次打开 /solution 直接展示
          updateTechnicalPath(path.id, mapTechPathToBackend({ ...techPath.value, images }, path.projectId))
            .then(() => console.log('[方案详情] 配图已写库:', { pathId: path.id }))
            .catch((e) => console.error('方案图片落库失败（静默）:', e))
        } else {
          console.warn('[方案详情] 接口返回成功但没拿到图片地址，页面会保持空白:', pollData)
        }
        return
      }
      if (pollData.status === 'FAILED') {
        console.warn('[方案详情] 生成方案图片失败:', pollData.error || '未知错误')
        return
      }
    }
    console.warn('[方案详情] 配图任务超时（180秒），页面保持空白')
  } catch (e) {
    console.error('[方案详情] 生成方案图片异常:', e)
  } finally {
    imageGenerating.value = false
  }
}
// 返回按钮点击事件
const back = () => {
  emit('back')
}

// TRIZ原理折叠面板激活状态
const activeNames = ref(['1'])
</script>

<style scoped lang="scss">
.tech-path-detail {
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
  min-height: 100%;
  margin: 0 auto;
}

.detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 60px 0;
  color: #666;
  font-size: 14px;

  .loading-icon {
    margin-right: 8px;
    animation: spin 0.8s linear infinite;
  }
}

/* 未生成完整详情：提示 + 生成按钮 */
.detail-missing {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 16px auto 0;
  padding: 16px 20px;
  max-width: 960px;
  width: 100%;
  box-sizing: border-box;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;

  &__text {
    h3 {
      margin: 0 0 6px;
      font-size: 15px;
      color: #614700;
    }
    p {
      margin: 0;
      font-size: 13px;
      color: #8c6d1f;
      line-height: 1.5;
    }
  }

  &__btn {
    flex-shrink: 0;
  }
}

/* 正在生成完整详情 */
.detail-generating {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 40px auto;
  padding: 32px 24px;
  max-width: 960px;
  width: 100%;
  box-sizing: border-box;
  text-align: center;

  &__bar {
    position: relative;
    width: 160px;
    height: 3px;
    border-radius: 2px;
    background: #eef1f5;
    overflow: hidden;

    span {
      position: absolute;
      top: 0;
      left: -40%;
      width: 40%;
      height: 100%;
      border-radius: 2px;
      background: #5b7fae;
      animation: detail-loading-slide 1.5s ease-in-out infinite;
    }
  }

  h3 {
    margin: 4px 0 0;
    font-size: 16px;
    color: #333;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: #666;
  }
}

.detail-error-banner {
  max-width: 960px;
  margin: 16px auto 0;
  width: 100%;
  box-sizing: border-box;
}

.detail-error {
  padding: 40px;

  &__actions {
    margin-top: 16px;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes detail-loading-slide {
  0% { left: -40%; }
  100% { left: 100%; }
}

/* 返回按钮样式 */
.back-header {
  padding: 16px;
  background-color: #ffffff;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  .el-button {
    color: #1890ff;
    font-size: 14px;
    padding: 0;
  }
}

/* 头部区域 */
.detail-header {
  background-color: #ffffff;
  padding: 30px 40px;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.header-content {
  // margin: 0 auto;
}

.path-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.path-description {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 16px 0;
}

.path-tag {
  margin-top: 8px;
  margin-bottom: 12px;
}

.path-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  
  .tag-item {
    background-color: #f0f9ff;
    color: #1890ff;
    border-color: #90caf9;
  }
}

/* 主要内容区域 */
.detail-content {
  padding: 30px 0px;
  flex: 1;
  box-sizing: border-box;
}

/* 通用区块样式 */
.section {
  margin-bottom: 30px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
  padding-bottom: 20px;
}

/* 方案详情图片 */
.image-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.detail-image {
  background-color: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  overflow: hidden;
}

.image-item {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* 破图兜底：地址失效时给个可见提示，而不是留一个纯白框 */
.image-item--broken {
  min-width: 60%;
  min-height: 60%;
  background-image: linear-gradient(135deg, #f0f0f0 25%, #e6e6e6 25%, #e6e6e6 50%, #f0f0f0 50%, #f0f0f0 75%, #e6e6e6 75%);
  background-size: 16px 16px;
  border: 1px dashed #d0d0d0;
  border-radius: 6px;
}

.image-loading-text {
  margin-bottom: 12px;
  color: #909399;
  font-size: 13px;
}

/* 技术逻辑和结构方案 */
.logic-card,
.structure-card {
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.logic-text,
.structure-text {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* 多段渲染时段落间留间距 */
.logic-text + .logic-text,
.structure-text + .structure-text {
  margin-top: 12px;
}

/* 适用场景 */
.scenario-card {
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.scenario-list {
  margin: 0;
  padding-left: 20px;
}

.scenario-item {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

/* 工程落地要点 */
.engineering-card {
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.engineering-list {
  margin: 0;
  padding-left: 20px;
}

.engineering-item {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

/* 风险点与缓解措施 */
.risk-card {
  background-color: #fff5f5;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 20px;
}

.risk-list {
  margin: 0;
  padding-left: 20px;
}

.risk-item {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  strong {
    color: #f56c6c;
  }
}

/* 优缺点分析 */
.pros-cons-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.pros-card {
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  padding: 20px;
}

.pros-title {
  font-size: 16px;
  font-weight: 600;
  color: #52c41a;
  margin: 0 0 12px 0;
}

.pros-list,
.cons-list {
  margin: 0;
  padding-left: 20px;
}

.pros-item,
.cons-item {
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 8px;
}

.pros-item {
  color: #52c41a;
}

.cons-item {
  color: #fa8c16;
}

.cons-card {
  background-color: #fff7e6;
  border: 1px solid #ffe7ba;
  border-radius: 8px;
  padding: 20px;
}

.cons-title {
  font-size: 16px;
  font-weight: 600;
  color: #fa8c16;
  margin: 0 0 12px 0;
}

/* TRIZ原理 */
.triz-grid {
  margin-top: 16px;
}

:deep(.el-collapse) {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-collapse-item) {
  border-bottom: 1px solid #e8e8e8;
  
  &:last-child {
    border-bottom: none;
  }
}

:deep(.el-collapse-item__header) {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  background-color: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  &.is-active {
    background-color: #e6f7ff;
    border-bottom-color: #91d5ff;
  }
}

:deep(.el-collapse-item__content) {
  padding: 20px;
  background-color: #ffffff;
  border-bottom: none;
}

.triz-content {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  padding: 0 10px;

  .triz-para {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

/* 原理总结 */
.summary-card {
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.summary-text {
  font-size: 14px;
  color: #595959;
  line-height: 1.7;
  margin: 0;
  text-indent: 2em;
  
  &::before {
    content: "✓ ";
    color: #52c41a;
    font-weight: bold;
  }
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .patent-grid {
    grid-template-columns: 1fr;
  }
  
  .image-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .detail-header {
    padding: 20px;
  }
  
  .detail-content {
    padding: 20px;
  }
  
  .path-title {
    font-size: 20px;
  }
  
  .path-description {
    font-size: 14px;
  }
  
  .section {
    padding: 16px;
  }
  
  .section-title {
    font-size: 18px;
  }
  
  .image-grid {
    grid-template-columns: 1fr;
  }
  
  .pros-cons-grid {
    grid-template-columns: 1fr;
  }
}
</style>
